/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Field,
    Input,
    Link,
    Select,
    Spinner,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    makeStyles,
    tokens
} from '@fluentui/react-components';
import { Add24Regular, Dismiss24Regular, Edit24Regular, Info24Regular } from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import type { AnsprechpartnerItem } from '../../models/AnsprechpartnerItem';
import type { IDataItem } from '../../models/IDataItem';
import type { ProjektPartnerItem } from '../../models/ProjektPartnerItem';
import { DataService } from '../../services/dataService';

interface ProjektpartnerDrawerProps {
    isOpen: boolean;
    partnerId: number | null;
    isEditMode: boolean;
    onClose: () => void;
    onSave?: (partner: ProjektPartnerItem) => void;
}

const useStyles = makeStyles({
    section: {
        marginBottom: tokens.spacingVerticalL,
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground1,
    },
    formGroup: {
        display: 'grid',
        gap: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalL,
    },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalM,
    },
    fullWidth: {
        gridColumn: '1 / -1',
    },
    switchField: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    tableContainer: {
        marginTop: tokens.spacingVerticalM,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        overflow: 'auto',
    },
    compactTable: {
        fontSize: tokens.fontSizeBase200,
        '& th, & td': {
            padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        '& th': {
            whiteSpace: 'nowrap',
        },
    },
    truncatedCell: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
    },
    addContactLink: {
        marginTop: tokens.spacingVerticalM,
        cursor: 'pointer',
    },
    footerActions: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        justifyContent: 'flex-start',
        marginTop: tokens.spacingVerticalL,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: tokens.spacingVerticalL,
    },
});

const ProjektpartnerDrawer: React.FC<ProjektpartnerDrawerProps> = ({
    isOpen,
    partnerId,
    isEditMode,
    onClose,
    onSave,
}) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [partner, setPartner] = useState<ProjektPartnerItem | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [editedPartner, setEditedPartner] = useState<Partial<ProjektPartnerItem> | null>(null);
    const [nations, setNations] = useState<IDataItem[]>([]);
    const [finanzCodes, setFinanzCodes] = useState<IDataItem[]>([]);
    const [koopCodes, setKoopCodes] = useState<IDataItem[]>([]);

    // Contact drawer state
    const [isContactDrawerOpen, setIsContactDrawerOpen] = useState(false);
    const [contactDrawerMode, setContactDrawerMode] = useState<'add' | 'edit'>('add');
    const [editingContactId, setEditingContactId] = useState<number | null>(null);
    const [contactFormAnrede, setContactFormAnrede] = useState(false);
    const [contactFormTitelVor, setContactFormTitelVor] = useState('');
    const [contactFormVorname, setContactFormVorname] = useState('');
    const [contactFormNachname, setContactFormNachname] = useState('');
    const [contactFormTitelNach, setContactFormTitelNach] = useState('');
    const [contactFormFunktion, setContactFormFunktion] = useState('');
    const [contactFormEmail, setContactFormEmail] = useState('');
    const [contactFormTelefon, setContactFormTelefon] = useState('');
    const [contactFormIsAktiv, setContactFormIsAktiv] = useState(true);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [contactToDeleteId, setContactToDeleteId] = useState<number | null>(null);

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch partner details or initialize for new partner
    useEffect(() => {
        if (!isOpen || !dataService) {
            return;
        }

        if (partnerId) {
            // Existing partner - fetch details
            const fetchPartnerDetails = async () => {
                try {
                    setIsLoading(true);
                    const partnerDetails = await dataService.getProjektpartnerById(partnerId);
                    setPartner(partnerDetails || null);
                    setEditedPartner(partnerDetails ? { ...partnerDetails } : null);
                } catch (err) {
                    console.error('Error loading partner details:', err);
                    setPartner(null);
                    setEditedPartner(null);
                } finally {
                    setIsLoading(false);
                }
            };

            fetchPartnerDetails();
        } else {
            // New partner - initialize with empty values
            const emptyPartner: Partial<ProjektPartnerItem> = {
                partnerID: 0,
                itemId: 0,
                bezeichnung1: '',
                bezeichnung2: '',
                strasse: '',
                plz: '',
                ort: '',
                nation: '',
                nationID: undefined,
                websiteUrl: '',
                uidNummer: '',
                finanzPartnerCode: undefined,
                koopPartnerCode: undefined,
                isFoerdergeber: false,
                anzeigeWebsite: false,
                keinImportCRM: false,
                ansprechpersonen: [],
            };
            setPartner(null);
            setEditedPartner(emptyPartner);
        }
    }, [isOpen, partnerId, dataService]);

    // Fetch dropdown options
    useEffect(() => {
        if (!isOpen || !dataService) {
            return;
        }

        const fetchOptions = async () => {
            try {
                const [nationsData, finanzCodesData, koopCodesData] = await Promise.all([
                    dataService.getNations(),
                    dataService.getFinanzPartnerCodes(),
                    dataService.getKooperationsPartnerCodes(),
                ]);
                setNations(nationsData || []);
                setFinanzCodes(finanzCodesData || []);
                setKoopCodes(koopCodesData || []);
            } catch (err) {
                console.error('Error loading dropdown options:', err);
            }
        };

        fetchOptions();
    }, [isOpen, dataService]);

    const handleInputChange = (field: keyof ProjektPartnerItem, value: any) => {
        if ((isEditMode || !partnerId) && editedPartner) {
            setEditedPartner({
                ...editedPartner,
                [field]: value,
            });
        }
    };

    const isRequiredFieldsMissing = (): boolean => {
        if (!editedPartner) return true;

        return (
            !editedPartner.bezeichnung1 ||
            editedPartner.bezeichnung1.trim() === '' ||
            !editedPartner.strasse ||
            editedPartner.strasse.trim() === '' ||
            !editedPartner.ort ||
            editedPartner.ort.trim() === '' ||
            !editedPartner.nationID ||
            editedPartner.nationID === 0
        );
    };

    const handleSave = async () => {
        if (!editedPartner || !dataService) {
            return;
        }

        try {
            setIsLoading(true);
            // Use addOrUpdateProjektpartner for both new and existing partners
            const partnerId = await dataService.addOrUpdateProjektpartner(editedPartner as ProjektPartnerItem);
            if (onSave) {
                // Create the saved partner object with the ID returned from API
                const savedPartner: ProjektPartnerItem = {
                    ...editedPartner,
                    partnerID: partnerId,
                } as ProjektPartnerItem;
                onSave(savedPartner);
            }
            onClose();
        } catch (err) {
            console.error('Error saving partner:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const resetContactForm = () => {
        setContactFormAnrede(false);
        setContactFormTitelVor('');
        setContactFormVorname('');
        setContactFormNachname('');
        setContactFormTitelNach('');
        setContactFormFunktion('');
        setContactFormEmail('');
        setContactFormTelefon('');
        setContactFormIsAktiv(true);
        setEditingContactId(null);
        setContactDrawerMode('add');
    };

    const handleOpenContactDrawerForEdit = (contact: AnsprechpartnerItem) => {
        setEditingContactId(contact.kontaktID || null);
        setContactDrawerMode('edit');
        setContactFormAnrede(contact.anrede === 'Herr');
        setContactFormTitelVor(contact.titel || '');
        setContactFormVorname(contact.vorname || '');
        setContactFormNachname(contact.nachname || '');
        setContactFormTitelNach(contact.titelAfter || '');
        setContactFormFunktion(contact.funktion || '');
        setContactFormEmail(contact.email || '');
        setContactFormTelefon(contact.telefon || '');
        setContactFormIsAktiv(contact.isAktiv || true);
        setIsContactDrawerOpen(true);
    };

    const handleSaveNewContact = async () => {
        if (!dataService || !partner?.partnerID) return;

        try {
            setIsLoading(true);
            const contactData = {
                kontaktID: editingContactId || 0,
                projektpartnerID: partner.partnerID,
                itemId: 0,
                anrede: contactFormAnrede ? 'Herr' : 'Frau',
                titel: contactFormTitelVor,
                vorname: contactFormVorname,
                nachname: contactFormNachname,
                titelAfter: contactFormTitelNach,
                name: `${contactFormVorname} ${contactFormNachname}`,
                fullNameWithGrades: `${contactFormTitelVor} ${contactFormVorname} ${contactFormNachname} ${contactFormTitelNach}`.trim(),
                fullNameWithGradesUppercase: `${contactFormTitelVor} ${contactFormVorname} ${contactFormNachname} ${contactFormTitelNach}`.trim().toUpperCase(),
                title: contactFormFunktion,
                funktion: contactFormFunktion,
                description: '',
                email: contactFormEmail,
                emailAsLink: contactFormEmail,
                telefon: contactFormTelefon,
                anzahlProjekte: 0,
                value: contactFormNachname,
                text: `${contactFormVorname} ${contactFormNachname}`,
                selected: false,
                code: 0,
                visibility: 'public',
                isAktiv: contactFormIsAktiv,
                isAktivAsText: contactFormIsAktiv ? 'Active' : 'Inactive',
            };

            await dataService.addOrUpdateAnsprechpartner(contactData);
            resetContactForm();
            setIsContactDrawerOpen(false);

            // Fetch updated partner to refresh contacts list
            const updatedPartner = await dataService.getProjektpartnerById(partner.partnerID);
            if (updatedPartner) {
                setPartner(updatedPartner);
                setEditedPartner({ ...updatedPartner });
            }
        } catch (err) {
            console.error('Error saving contact:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteContact = async () => {
        if (!dataService || !contactToDeleteId || !partner?.partnerID) return;

        try {
            console.log('Deleting contact with ID:', contactToDeleteId);
            setIsDeleting(true);
            await dataService.deleteAnsprechpartner(contactToDeleteId);
            setIsDeleteDialogOpen(false);
            setContactToDeleteId(null);
            resetContactForm();
            setIsContactDrawerOpen(false);

            // Fetch updated partner to refresh contacts list
            const updatedPartner = await dataService.getProjektpartnerById(partner.partnerID);
            if (updatedPartner) {
                setPartner(updatedPartner);
                setEditedPartner({ ...updatedPartner });
            }
        } catch (err) {
            console.error('Error deleting contact:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteDialogOpen(false);
        setContactToDeleteId(null);
    };

    // When creating new partner (!partnerId), always use editedPartner. When editing, use editedPartner if isEditMode, else partner
    const displayPartner = !partnerId ? editedPartner : (isEditMode ? editedPartner : partner);
    const contacts = displayPartner?.ansprechpersonen || [];
    console.log('Partner & Contacts:', displayPartner, contacts);
    return (
        <>
            <Drawer
                open={isOpen}
                onOpenChange={(_, { open }) => {
                    if (!open) {
                        onClose();
                    }
                }}
                size="medium"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                icon={<Dismiss24Regular />}
                                onClick={onClose}
                            />
                        }
                    >
                        {partnerId ? 'Edit Partner' : 'Create New Partner'}
                    </DrawerHeaderTitle>
                </DrawerHeader>

                <DrawerBody>
                    {isLoading ? (
                        <div className={styles.loadingContainer}>
                            <Spinner label="Loading..." />
                        </div>
                    ) : !displayPartner ? (
                        <div>Partner not found</div>
                    ) : (
                        <>
                            {/* Section 1: Company/Institution Data */}
                            <div className={styles.section}>
                                <div className={styles.sectionTitle}>Company/Institution Data</div>
                                <div className={styles.formGroup}>
                                    <Field label="Company/Institution (Line 1)" required>
                                        <Input
                                            value={displayPartner.bezeichnung1 || ''}
                                            onChange={(_, data) =>
                                                handleInputChange('bezeichnung1', data.value)
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        />
                                    </Field>
                                    <Field label="Company/Institution (Line 2)">
                                        <Input
                                            value={displayPartner.bezeichnung2 || ''}
                                            onChange={(_, data) =>
                                                handleInputChange('bezeichnung2', data.value)
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        />
                                    </Field>
                                    <Field label="Street" required>
                                        <Input
                                            value={displayPartner.strasse || ''}
                                            onChange={(_, data) =>
                                                handleInputChange('strasse', data.value)
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        />
                                    </Field>
                                    <div className={styles.fieldRow}>
                                        <Field label="Postal Code">
                                            <Input
                                                value={displayPartner.plz || ''}
                                                onChange={(_, data) =>
                                                    handleInputChange('plz', data.value)
                                                }
                                                disabled={!isEditMode && !!partnerId}
                                            />
                                        </Field>
                                        <Field label="City" required>
                                            <Input
                                                value={displayPartner.ort || ''}
                                                onChange={(_, data) =>
                                                    handleInputChange('ort', data.value)
                                                }
                                                disabled={!isEditMode && !!partnerId}
                                            />
                                        </Field>
                                    </div>
                                    <Field label="Country" required>
                                        <Select
                                            value={displayPartner.nationID?.toString() || ''}
                                            onChange={(_, data) =>
                                                handleInputChange('nationID', parseInt(data.value))
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        >
                                            <option value="">Select Country</option>
                                            {nations.map((nation: IDataItem) => (
                                                <option key={nation.value} value={nation.value.toString()}>
                                                    {nation.text}
                                                </option>
                                            ))}
                                        </Select>
                                    </Field>
                                </div>
                            </div>

                            {/* Section 2: Additional Data */}
                            <div className={styles.section}>
                                <div className={styles.sectionTitle}>Additional Data</div>
                                <div className={styles.formGroup}>
                                    <Field label="Website Address">
                                        <Input
                                            value={displayPartner.websiteUrl || ''}
                                            onChange={(_, data) =>
                                                handleInputChange('websiteUrl', data.value)
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        />
                                    </Field>
                                    <Field label="UID Number">
                                        <Input
                                            value={displayPartner.uidNummer || ''}
                                            onChange={(_, data) =>
                                                handleInputChange('uidNummer', data.value)
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        />
                                    </Field>
                                    <Field label="Finance Partner Code">
                                        <Select
                                            value={displayPartner.finanzPartnerCode?.toString() || ''}
                                            onChange={(_, data) =>
                                                handleInputChange(
                                                    'finanzPartnerCode',
                                                    parseInt(data.value)
                                                )
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        >
                                            <option value="">Select Code</option>
                                            {finanzCodes.map((code: IDataItem) => (
                                                <option key={code.value} value={code.value.toString()}>
                                                    {code.text}
                                                </option>
                                            ))}
                                        </Select>
                                    </Field>
                                    <Field label="Cooperation Partner Code">
                                        <Select
                                            value={displayPartner.koopPartnerCode?.toString() || ''}
                                            onChange={(_, data) =>
                                                handleInputChange(
                                                    'koopPartnerCode',
                                                    parseInt(data.value)
                                                )
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        >
                                            <option value="">Select Code</option>
                                            {koopCodes.map((code: IDataItem) => (
                                                <option key={code.value} value={code.value.toString()}>
                                                    {code.text}
                                                </option>
                                            ))}
                                        </Select>
                                    </Field>
                                    <div className={styles.switchField}>
                                        <Switch
                                            checked={displayPartner.isFoerdergeber || false}
                                            onChange={(_, data) =>
                                                handleInputChange('isFoerdergeber', data.checked)
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        />
                                        <span>Funding Provider</span>
                                    </div>
                                    <div className={styles.switchField}>
                                        <Switch
                                            checked={displayPartner.anzeigeWebsite || false}
                                            onChange={(_, data) =>
                                                handleInputChange('anzeigeWebsite', data.checked)
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        />
                                        <span>Display on Website</span>
                                    </div>
                                    <div className={styles.switchField}>
                                        <Switch
                                            checked={displayPartner.keinImportCRM || false}
                                            onChange={(_, data) =>
                                                handleInputChange('keinImportCRM', data.checked)
                                            }
                                            disabled={!isEditMode && !!partnerId}
                                        />
                                        <span>Exclude from IMCPartners</span>
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Ansprechpersonen */}
                            {partnerId ? (
                                <div className={styles.section}>
                                    <div className={styles.sectionTitle}>Contacts</div>
                                    {contacts.length > 0 ? (
                                        <div className={styles.tableContainer}>
                                            <Table className={styles.compactTable}
                                                size="small">
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHeaderCell style={{ width: '32px', minWidth: '32px' }}>Edit</TableHeaderCell>
                                                        <TableHeaderCell style={{ width: '35%', minWidth: '80px' }}>Name</TableHeaderCell>
                                                        <TableHeaderCell style={{ width: '35%', minWidth: '80px' }}>Email</TableHeaderCell>
                                                        <TableHeaderCell style={{ width: '15%', minWidth: '40px' }} title="Active">Active</TableHeaderCell>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {contacts.map((contact: AnsprechpartnerItem, idx: number) => (
                                                        <TableRow
                                                            key={idx}
                                                            onDoubleClick={() => handleOpenContactDrawerForEdit(contact)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <TableCell>
                                                                <Button
                                                                    icon={<Edit24Regular />}
                                                                    appearance="subtle"
                                                                    onClick={() => handleOpenContactDrawerForEdit(contact)}
                                                                    title="Edit contact"
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className={styles.truncatedCell} title={`${contact.anrede || ''} ${contact.name || '-'}`}>
                                                                    {contact.anrede || ''} {contact.name || '-'}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                {contact.email ? (
                                                                    <Link href={`mailto:${contact.email}`} className={styles.truncatedCell} title={contact.email}>
                                                                        {contact.email}
                                                                    </Link>
                                                                ) : (
                                                                    <div className={styles.truncatedCell}>-</div>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {contact.isAktiv ? 'Yes' : 'No'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div>No contacts available</div>
                                    )}
                                    <Link
                                        onClick={() => {
                                            resetContactForm();
                                            setContactDrawerMode('add');
                                            setIsContactDrawerOpen(true);
                                        }}
                                        className={styles.addContactLink}
                                    >
                                        <Add24Regular style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                        Add Contact
                                    </Link>
                                </div>
                            ) : (
                                <div className={styles.section} style={{ backgroundColor: tokens.colorNeutralBackground3, padding: tokens.spacingHorizontalL, borderRadius: tokens.borderRadiusMedium, border: `1px solid ${tokens.colorNeutralStroke2}` }}>
                                    <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-start' }}>
                                        <Info24Regular style={{ marginTop: '4px', color: tokens.colorBrandForeground1, flexShrink: 0 }} />
                                        <div style={{ color: tokens.colorNeutralForeground1 }}>
                                            <div style={{ fontWeight: 600, marginBottom: tokens.spacingVerticalXS }}>Contacts</div>
                                            <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2 }}>
                                                Save the partner first to add contacts.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Footer Actions */}
                            {(isEditMode || !partnerId) && (
                                <div className={styles.footerActions}>
                                    <Button appearance="primary" onClick={handleSave} disabled={isLoading || isRequiredFieldsMissing()}>
                                        {partnerId ? 'Save' : 'Add Partner'}
                                    </Button>
                                    <Button appearance="secondary" onClick={onClose}>
                                        Cancel
                                    </Button>

                                </div>
                            )}
                        </>
                    )}
                </DrawerBody>
            </Drawer>

            {/* Contact Drawer */}
            <Drawer
                open={isContactDrawerOpen}
                onOpenChange={(_, { open }) => {
                    if (!open) {
                        resetContactForm();
                        setIsContactDrawerOpen(false);
                    }
                }}
                size="medium"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                icon={<Dismiss24Regular />}
                                onClick={() => {
                                    resetContactForm();
                                    setIsContactDrawerOpen(false);
                                }}
                            />
                        }
                    >
                        {contactDrawerMode === 'edit' ? 'Edit Contact' : 'Add Contact'}
                    </DrawerHeaderTitle>
                </DrawerHeader>

                <DrawerBody>
                    {isLoading ? (
                        <div className={styles.loadingContainer}>
                            <Spinner label="Saving..." />
                        </div>
                    ) : (
                        <>
                            <div className={styles.formGroup}>
                                <Field label="Salutation">
                                    <Switch
                                        checked={contactFormAnrede}
                                        onChange={(_, data) =>
                                            setContactFormAnrede(data.checked)
                                        }
                                        label={contactFormAnrede ? 'Mr.' : 'Ms.'}
                                    />
                                </Field>
                            </div>

                            <div className={styles.formGroup}>
                                <Field label="Title before Name">
                                    <Input
                                        type="text"
                                        value={contactFormTitelVor}
                                        onChange={(_, data) => setContactFormTitelVor(data.value)}
                                    />
                                </Field>
                            </div>

                            <div className={styles.formGroup}>
                                <Field label="First Name">
                                    <Input
                                        type="text"
                                        value={contactFormVorname}
                                        onChange={(_, data) => setContactFormVorname(data.value)}
                                    />
                                </Field>
                            </div>

                            <div className={styles.formGroup}>
                                <Field label="Last Name">
                                    <Input
                                        type="text"
                                        value={contactFormNachname}
                                        onChange={(_, data) => setContactFormNachname(data.value)}
                                    />
                                </Field>
                            </div>

                            <div className={styles.formGroup}>
                                <Field label="Title after Name">
                                    <Input
                                        type="text"
                                        value={contactFormTitelNach}
                                        onChange={(_, data) => setContactFormTitelNach(data.value)}
                                    />
                                </Field>
                            </div>

                            <div className={styles.formGroup}>
                                <Field label="Position">
                                    <Input
                                        type="text"
                                        value={contactFormFunktion}
                                        onChange={(_, data) => setContactFormFunktion(data.value)}
                                    />
                                </Field>
                            </div>

                            <div className={styles.formGroup}>
                                <Field label="Email">
                                    <Input
                                        type="email"
                                        value={contactFormEmail}
                                        onChange={(_, data) => setContactFormEmail(data.value)}
                                    />
                                </Field>
                            </div>

                            <div className={styles.formGroup}>
                                <Field label="Phone">
                                    <Input
                                        type="tel"
                                        value={contactFormTelefon}
                                        onChange={(_, data) => setContactFormTelefon(data.value)}
                                    />
                                </Field>
                            </div>

                            <div className={styles.formGroup}>
                                <Field label="Status">
                                    <div className={styles.switchField}>

                                        <Switch
                                            checked={contactFormIsAktiv}
                                            onChange={(_, data) =>
                                                setContactFormIsAktiv(data.checked)
                                            }
                                        />
                                        <span>{contactFormIsAktiv ? 'Active' : 'Inactive'}</span>

                                    </div>
                                </Field>
                            </div>

                            <div className={styles.footerActions}>
                                {contactDrawerMode === 'edit' && (
                                    <Button
                                        appearance="secondary"
                                        onClick={() => {
                                            setContactToDeleteId(editingContactId);
                                            setIsDeleteDialogOpen(true);
                                        }}
                                    >
                                        Delete
                                    </Button>
                                )}
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: tokens.spacingHorizontalM }}>
                                    <Button
                                        appearance="secondary"
                                        onClick={() => {
                                            resetContactForm();
                                            setIsContactDrawerOpen(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        appearance="primary"
                                        onClick={handleSaveNewContact}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </DrawerBody>
            </Drawer>

            {/* Delete Contact Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onOpenChange={(_event, data) => {
                    if (!data.open) handleCancelDelete();
                }}
            >
                <DialogSurface>
                    <DialogContent>
                        <DialogTitle>Delete Contact</DialogTitle>
                        <DialogBody>
                            <div style={{ marginBottom: tokens.spacingVerticalM }}>
                                Are you sure you want to delete the contact "{contactFormVorname} {contactFormNachname}"?
                            </div>
                            <div style={{ marginBottom: tokens.spacingVerticalM, color: tokens.colorStatusWarningForeground1 }}>
                                This action cannot be undone.
                            </div>
                        </DialogBody>
                        <div style={{
                            display: 'flex',
                            gap: tokens.spacingHorizontalM,
                            justifyContent: 'flex-end',
                            padding: tokens.spacingVerticalL,
                            borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
                        }}>
                            <Button
                                appearance="secondary"
                                onClick={handleCancelDelete}
                                disabled={isDeleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleDeleteContact}
                                disabled={isDeleting}
                            >
                                {isDeleting ? <Spinner size="small" /> : 'Delete'}
                            </Button>
                        </div>
                    </DialogContent>
                </DialogSurface>
            </Dialog>
        </>
    );
};

export default ProjektpartnerDrawer;
